/**
 * ProjectInfoPanel - A vanilla component for editing project info
 *
 * Props:
 * - title: The project title
 * - question: The focal question
 *
 * Events:
 * - title-input: Dispatched when title changes (detail: string)
 * - question-input: Dispatched when question changes (detail: string)
 */
import * as Styles from "../../../styles.ts";
import { css, html, LitElement } from "lit";
import { customElement, property } from "lit/decorators.js";
import { customEvent } from "../../../util/event.ts";

// Components
import "../../dui/page-inset/dui-page-inset.ts";
import "../../dui/vstack/dui-vstack.ts";
import "../../dui/field/dui-field.ts";
import "../../dui/field/dui-field-label.ts";
import "../../dui/field/dui-field-description.ts";
import "../../dui/input/dui-input.ts";

/** Event dispatched when project title changes */
export const projectTitleInputEvent = customEvent<string>(
  "project-title-input",
  {
    bubbles: true,
    composed: true,
  },
);

/** Event dispatched when focal question changes */
export const focalQuestionInputEvent = customEvent<string>(
  "focal-question-input",
  {
    bubbles: true,
    composed: true,
  },
);

const focalQuestionDesc = html`
  <p>
    A good focal question focuses on a critical uncertainty, defines a clear
    timeframe, and a clear context for&nbsp;exploration.
  </p>
`;

const style = css`
  :host {
    display: block;
    height: 100cqh;
    width: 100cqw;
    container-type: size;
  }

  .title-textfield,
  .focal-question-textfield {
    --textfield-font-size: var(--font-size-xl);
    --textfield-line-height: var(--line-height-snug);
  }
`;

@customElement("project-info-panel")
export class ProjectInfoPanelElement extends LitElement {
  static override styles = [Styles.base, style];

  /** Project title */
  @property({ type: String })
  accessor projectTitle = "";

  /** Focal question */
  @property({ type: String })
  accessor focalQuestion = "";

  #onTitleInput = (event: CustomEvent<{ value: string }>): void => {
    this.dispatchEvent(projectTitleInputEvent(event.detail.value));
  };

  #onQuestionInput = (event: CustomEvent<{ value: string }>): void => {
    this.dispatchEvent(focalQuestionInputEvent(event.detail.value));
  };

  override render() {
    return html`
      <dui-page-inset>
        <dui-vstack>
          <dui-field>
            <dui-field-label>Title</dui-field-label>
            <dui-input
              class="title-textfield"
              @input-change="${this.#onTitleInput}"
              .value="${this.projectTitle}"
              placeholder="Title"
            ></dui-input>
          </dui-field>
          <dui-field>
            <dui-field-label>Focal question</dui-field-label>
            <dui-input
              class="focal-question-textfield"
              @input-change="${this.#onQuestionInput}"
              .value="${this.focalQuestion}"
              placeholder="What will x region look like in y years?"
            ></dui-input>
            <dui-field-description>${focalQuestionDesc}</dui-field-description>
          </dui-field>
        </dui-vstack>
      </dui-page-inset>
    `;
  }
}
